import { ChangeEvent, useState } from 'react';

import { useMount } from 'react-use';

import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';

import first from 'lodash/first';

import DialogTitleWithX from '@/components/DialogTitleWithX';
import StatusBadge from '@/components/StatusBadge';
import { DialogActions } from '@/sections/Dialog/styled';
import { PROD_ENV_NAME } from '@/sections/Workspace/utils';
import { DialogProps } from '@toolpad/core/useDialogs';

import { ConfigContainer, ConfigHeader } from './styled';
import { PromoteConfirmationDialogPayload, PromotionConfigState } from './types';

function PromoteConfirmationDialog({
  payload: { stableEnvironments, deployment },
  open,
  onClose,
}: DialogProps<PromoteConfirmationDialogPayload, PromotionConfigState>) {
  const [env, setEnv] = useState(null);
  const [shouldScaleDownPreviousDeployment, setShouldScaleDownPreviousDeployment] = useState(true);

  // Currently, production does not exist as a stable environment. In the case where the
  // env is None, we default to production
  const selectedEnvName =
    env || (!env && !deployment.isPrimary ? PROD_ENV_NAME : first(stableEnvironments)?.name);

  useMount(() => {
    if (selectedEnvName) {
      setEnv(selectedEnvName);
    }
  });

  function handleShouldScaleDownToggle(ev: ChangeEvent<HTMLInputElement>) {
    setShouldScaleDownPreviousDeployment(ev.target.checked);
  }

  function handleEnvChange(ev: SelectChangeEvent<string>) {
    setEnv(ev.target.value);
  }

  function handleClose(confirmed: boolean) {
    onClose({ confirmed, env, shouldScaleDownPreviousDeployment });
  }

  return (
    <Dialog
      open={open}
      onClose={() => handleClose(false)}
      aria-labelledby="promote-deployment-title"
      aria-describedby="promote-deployment-description"
      slotProps={{ paper: { sx: { width: 700 } } }}
    >
      <DialogTitleWithX onClose={() => handleClose(false)}>Promote deployment</DialogTitleWithX>
      <DialogContent sx={{ mt: 2 }}>
        <Stack gap={3}>
          <Stack gap={2}>
            <Typography variant="h5">You’re promoting the deployment:</Typography>
            <ConfigContainer>
              <ConfigHeader>
                <Typography variant="h5">#{deployment.id}</Typography>
                <StatusBadge status={deployment.status} />
              </ConfigHeader>
            </ConfigContainer>
          </Stack>
          <Stack gap={2}>
            <Typography variant="h5">To the following environment:</Typography>
            <ConfigContainer>
              <Stack gap={1} p={2}>
                <Select fullWidth value={selectedEnvName} onChange={handleEnvChange}>
                  <MenuItem
                    disabled={deployment.isPrimary}
                    key={PROD_ENV_NAME}
                    value={PROD_ENV_NAME}
                  >
                    production
                  </MenuItem>
                  {stableEnvironments?.map((env) => (
                    <MenuItem
                      disabled={deployment.id === env.currentChainDeployment?.id}
                      key={env.name}
                      value={env.name}
                    >
                      {env.name}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            </ConfigContainer>
          </Stack>
          <Stack gap={1}>
            <FormControlLabel
              onChange={handleShouldScaleDownToggle}
              control={<Checkbox size="small" checked={shouldScaleDownPreviousDeployment} />}
              label={<Typography variant="h5">Scale previous deployment to zero</Typography>}
            />
            <Typography variant="body1" sx={{ ml: 3 }}>
              The min number of replicas for the current production deployment will be set to 0. The
              other autoscaling settings will remain unchanged
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => handleClose(false)}
          data-cy="confirmation-promote-deployment-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          autoFocus={false}
          onClick={() => handleClose(true)}
          data-cy="confirmation-promote-deployment-ok"
        >
          Promote
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PromoteConfirmationDialog;
